import type {
  LlmTraceAsyncDelegationFlow,
  LlmTraceAsyncJob,
  LlmTraceCitationWorkerFlow,
  LlmTraceMainFlow,
  LlmTraceResponse,
  LlmTraceSubAgentFlow,
} from "@/types/llmTrace";
import { arrayValue, intValue, mapValue, numberValue, stringValue } from "@/utils/coerce";
import { durationMsOf } from "@/utils/toolCalls";
import {
  countNodes,
  laneBounds,
  msBetween,
  normalizeStatus,
  overlappingGroups,
  subtractIntervals,
  type NodeTokens,
  type NormalizedTrace,
  type TraceLane,
  type TraceLaneKind,
  type TraceMetrics,
  type TraceNode,
} from "./model";

/**
 * When a turn was waiting rather than running one of its own tools.
 *
 * The window between the end of inference and the end of the turn is not all
 * "waiting" — most of it is a tool executing. Only the gaps are.
 */
function waitSegmentsFor(
  inferenceEndMs: number | null,
  endMs: number | null,
  children: TraceNode[],
) {
  if (inferenceEndMs === null || endMs === null) return [];
  return subtractIntervals(inferenceEndMs, endMs, children);
}

// `llm_turn.usage` stores the provider payload verbatim — Gemini writes
// snake_case from model_dump(), OpenRouter writes the OpenAI shape — so the key
// names differ per turn. These alias lists mirror the backend's own reader,
// app/services/credits.py extract_llm_token_usage(), including its camelCase
// variants. Citation-worker and sub-agent usage is always the Gemini shape.
const INPUT_TOKEN_KEYS = [
  "prompt_token_count",
  "promptTokenCount",
  "prompt_tokens",
  "input_tokens",
];
const OUTPUT_TOKEN_KEYS = [
  "candidates_token_count",
  "candidatesTokenCount",
  "completion_tokens",
  "output_tokens",
];
// Reasoning tokens. Missed entirely before, and on a reasoning model they can
// outweigh both of the above.
const THOUGHT_TOKEN_KEYS = [
  "thoughts_token_count",
  "thoughtsTokenCount",
  "thought_tokens",
];
const TOTAL_TOKEN_KEYS = [
  "total_token_count",
  "totalTokenCount",
  "total_tokens",
];
// Served from the context cache, and already counted inside the input total.
// OpenRouter nests these (`prompt_tokens_details`); the backend's reader has
// read the nested keys too since migration 0239.
const CACHED_TOKEN_KEYS = [
  "cached_content_token_count",
  "cachedContentTokenCount",
  "cached_input_tokens",
];
// Written to the cache on this call — also inside the input total, and billed
// at a premium. Only OpenRouter reports it (nested); Gemini's implicit cache
// has no write.
const CACHE_WRITE_TOKEN_KEYS = ["cache_write_input_tokens"];

function tokensFrom(usage: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = intValue(usage[key]);
    if (value !== null) return value;
  }
  return null;
}

/** A count the OpenAI shape buries one level down. */
function nestedTokens(usage: Record<string, unknown>, group: string, key: string) {
  return intValue(mapValue(usage[group])[key]);
}

/** One usage payload as provider-neutral counts. */
export function readTokens(usage: Record<string, unknown>): NodeTokens {
  return {
    input: tokensFrom(usage, INPUT_TOKEN_KEYS),
    cachedInput:
      tokensFrom(usage, CACHED_TOKEN_KEYS) ??
      nestedTokens(usage, "prompt_tokens_details", "cached_tokens"),
    cacheWriteInput:
      tokensFrom(usage, CACHE_WRITE_TOKEN_KEYS) ??
      nestedTokens(usage, "prompt_tokens_details", "cache_write_tokens"),
    costUsd: numberValue(usage.cost),
    output: tokensFrom(usage, OUTPUT_TOKEN_KEYS),
    thoughts:
      tokensFrom(usage, THOUGHT_TOKEN_KEYS) ??
      nestedTokens(usage, "completion_tokens_details", "reasoning_tokens"),
    total: tokensFrom(usage, TOTAL_TOKEN_KEYS),
  };
}

type TokenTally = NodeTokens;

function emptyTally(): TokenTally {
  return {
    input: null,
    cachedInput: null,
    cacheWriteInput: null,
    costUsd: null,
    output: null,
    thoughts: null,
    total: null,
  };
}

function addUsage(tally: TokenTally, usage: Record<string, unknown>) {
  const tokens = readTokens(usage);
  tally.input = addTokens(tally.input, tokens.input);
  tally.cachedInput = addTokens(tally.cachedInput, tokens.cachedInput);
  tally.cacheWriteInput = addTokens(tally.cacheWriteInput, tokens.cacheWriteInput);
  tally.costUsd = addTokens(tally.costUsd, tokens.costUsd);
  tally.output = addTokens(tally.output, tokens.output);
  tally.thoughts = addTokens(tally.thoughts, tokens.thoughts);
  tally.total = addTokens(tally.total, tokens.total);
}

function addTokens(total: number | null, next: number | null) {
  if (next === null) return total;
  return (total ?? 0) + next;
}

function durationOf(startMs: number | null, endMs: number | null) {
  if (startMs === null || endMs === null) return null;
  return endMs - startMs;
}

function buildMetrics(
  lanes: TraceLane[],
  ttftMs: number | null,
  totalMs: number | null,
  tokens: TokenTally,
): TraceMetrics {
  return {
    ttftMs,
    totalMs,
    turnCount: countNodes(lanes, (node) => node.kind === "turn"),
    toolCount: countNodes(
      lanes,
      (node) => node.kind === "tool_call" || node.kind === "sa_tool_call",
    ),
    errorCount: countNodes(lanes, (node) => node.status === "error"),
    subAgentCount: lanes.filter((lane) => lane.kind === "sub_agent").length,
    citationWorkerCount: lanes.filter((lane) => lane.kind === "citation_worker")
      .length,
    tokensIn: tokens.input,
    tokensCached: tokens.cachedInput,
    tokensCacheWrite: tokens.cacheWriteInput,
    costUsd: tokens.costUsd,
    tokensOut: tokens.output,
    tokensThoughts: tokens.thoughts,
    tokensTotal: tokens.total,
  };
}

function byStart(a: TraceNode, b: TraceNode) {
  return (a.startMs ?? Number.MAX_SAFE_INTEGER) - (b.startMs ?? Number.MAX_SAFE_INTEGER);
}

/**
 * A turn's simultaneous tool calls, lifted into lanes of their own.
 *
 * Shared by the trace and replay paths. On the turn's row these bars are drawn
 * over one another and only the widest survives, so a batch is promoted the way
 * a sub-agent flow already is: its own lane, header and envelope, one row per
 * call. Everything else stays nested under the turn, which is what keeps a
 * serial trace drawn exactly as before — a group of one is not a batch.
 *
 * The caller must have worked out `waitSegments` already. They are computed
 * from every call the turn made, and a promoted call is still the turn waiting
 * on a tool of its own; doing it afterwards would pale the whole parallel
 * window and colour those milliseconds twice.
 */
function splitParallelCalls(turn: TraceNode): {
  batches: TraceLane[];
  remaining: TraceNode[];
} {
  const parallel = overlappingGroups(turn.children).filter(
    (group) => group.length > 1,
  );
  if (parallel.length === 0) return { batches: [], remaining: turn.children };

  const promoted = new Set(parallel.flat());
  const batches = parallel.map((group, index): TraceLane => {
    const bounds = laneBounds(group);
    return {
      id: `tb:${turn.id}:${index}`,
      kind: "tool_batch",
      label: "parallel tools",
      sublabel: `${turn.label} · ${group.length} calls`,
      startMs: bounds.startMs,
      endMs: bounds.endMs,
      parentNodeId: turn.id,
      status: group.some((node) => node.status === "error") ? "error" : "ok",
      rawStatus: "",
      detail: {
        turn_id: turn.id,
        tool_names: group.map((node) => node.label),
        call_count: group.length,
        // What running them together actually bought. The wall clock is the
        // span of the batch; the serial total is what the same calls would
        // have cost one after another.
        wall_clock_ms: durationOf(bounds.startMs, bounds.endMs),
        serial_total_ms: group.reduce(
          (sum, node) => sum + (node.durationMs ?? 0),
          0,
        ),
      },
      // Two batches in one turn are siblings, and TraceTimeline numbers them
      // from this the same way it numbers a dispatch's sub-agents.
      groupId: turn.id,
      nodes: [...group].sort(byStart),
    };
  });

  return {
    batches,
    remaining: turn.children.filter((node) => !promoted.has(node)),
  };
}

/**
 * Promote every turn's parallel batches, leaving the lane holding the rest.
 *
 * Bounds are taken before anything moves: the main loop still spans the work it
 * did, whichever lane that work ends up drawn on.
 */
function promoteBatches(nodes: TraceNode[]): TraceLane[] {
  const batches: TraceLane[] = [];
  for (const node of nodes) {
    const split = splitParallelCalls(node);
    if (split.batches.length === 0) continue;
    batches.push(...split.batches);
    node.children = split.remaining;
  }
  return batches;
}

const PARALLEL_NOTE =
  "병렬 배치의 종료 시각은 상한값입니다 — 결과를 호출 순서대로 수집하므로 먼저 끝난 호출도 앞 호출을 기다린 것으로 기록됩니다.";

// ---------------------------------------------------------------------------
// llm-trace endpoint
// ---------------------------------------------------------------------------

function mainLanesFromFlow(flow: LlmTraceMainFlow): TraceLane[] {
  const nodes = flow.turns.map((turn): TraceNode => {
    const children = turn.tool_calls.map((call): TraceNode => ({
      id: call.tool_call_id,
      kind: "tool_call",
      label: call.tool_name,
      startMs: call.offset_start_ms,
      endMs: call.offset_end_ms,
      status: normalizeStatus(call.status),
      rawStatus: call.status,
      durationMs: call.duration_ms,
      resultSlimmed: call.result_slimmed === true,
      // A `pending` call has no completed_at, so no end offset. Dropping it
      // would hide exactly the hang QA is trying to find.
      openEnded: call.offset_start_ms !== null && call.offset_end_ms === null,
      detail: call as unknown as Record<string, unknown>,
      children: [],
    }));
    return {
      id: turn.turn_id,
      kind: "turn",
      label: `turn ${turn.turn_index}`,
      startMs: turn.offset_start_ms,
      endMs: turn.offset_end_ms,
      inferenceEndMs: turn.inference_end_ms,
      waitSegments: waitSegmentsFor(turn.inference_end_ms, turn.offset_end_ms, children),
      status: turn.finish_reason === "error" ? "error" : "ok",
      rawStatus: turn.finish_reason ?? "",
      durationMs: turn.latency_ms,
      model: turn.model,
      provider: turn.provider,
      finalizeAnswer: turn.finalize_answer,
      openEnded: turn.offset_start_ms !== null && turn.offset_end_ms === null,
      detail: turn as unknown as Record<string, unknown>,
      tokens: readTokens(mapValue(turn.usage)),
      children,
    };
  });
  const bounds = laneBounds(nodes);
  return [
    {
      id: "main",
      kind: "main",
      label: "main",
      startMs: bounds.startMs,
      endMs: bounds.endMs,
      parentNodeId: null,
      nodes,
    },
    // Straight after main rather than interleaved by start offset with the
    // other flows: a batch is the main loop's own work, and reading it needs
    // the turn that launched it on the row above.
    ...promoteBatches(nodes),
  ];
}

function citationLaneFromFlow(
  flow: LlmTraceCitationWorkerFlow,
  index: number,
): TraceLane {
  return {
    id: `cw:${flow.worker_run_id}`,
    kind: "citation_worker",
    label: `citation #${index + 1}`,
    startMs: flow.offset_start_ms,
    endMs: flow.offset_end_ms,
    // The spawning tool call is the tighter link; fall back to the turn.
    parentNodeId: flow.parent?.tool_call_id ?? flow.parent?.turn_id ?? null,
    nodes: flow.spans.map((span): TraceNode => ({
      id: span.span_id,
      kind: "cw_span",
      label: span.span_kind,
      startMs: span.offset_start_ms,
      endMs: span.offset_end_ms,
      status: normalizeStatus(span.status),
      rawStatus: span.status,
      durationMs: span.duration_ms,
      model: span.model ?? undefined,
      provider: span.provider ?? undefined,
      detail: span as unknown as Record<string, unknown>,
      tokens: readTokens(mapValue(span.usage)),
      children: [],
    })),
  };
}

/**
 * The whole delegate_tasks call, as the lane its jobs are drawn inside.
 *
 * It holds no work of its own — every round and every tool call belongs to a
 * job below it — so its one node is an envelope spanning the jobs.
 */
function asyncDelegationLaneFromFlow(flow: LlmTraceAsyncDelegationFlow): TraceLane {
  const openJobs = flow.jobs.filter((job) => job.offset_end_ms === null).length;
  const detail = flow as unknown as Record<string, unknown>;
  return {
    id: `ad:${flow.parent.tool_call_id}`,
    kind: "async_delegation",
    label: flow.tool_name ?? "delegate_tasks",
    sublabel: `${flow.jobs.length} task(s)`,
    startMs: flow.offset_start_ms,
    endMs: flow.offset_end_ms,
    parentNodeId: flow.parent.tool_call_id,
    status: openJobs > 0 ? "running" : "ok",
    rawStatus: openJobs > 0 ? `${openJobs} running` : "completed",
    detail,
    nodes: [
      {
        id: `ad-envelope:${flow.parent.tool_call_id}`,
        kind: "async_delegation",
        label: flow.tool_name ?? "delegate_tasks",
        startMs: flow.offset_start_ms,
        endMs: flow.offset_end_ms,
        status: openJobs > 0 ? "running" : "ok",
        rawStatus: openJobs > 0 ? `${openJobs} running` : "completed",
        durationMs: durationOf(flow.offset_start_ms, flow.offset_end_ms),
        openEnded: flow.offset_start_ms !== null && flow.offset_end_ms === null,
        detail,
        children: [],
      },
    ],
  };
}

/**
 * One durable job and whatever it managed to run, on one lane.
 *
 * Job and execution are kept as one row rather than two nested ones: a split
 * multi-task call gives each job exactly one execution, so a separate level for
 * each would say the same thing twice and cost a third indent. The job is the
 * envelope and the execution is painted over it — which makes the queue wait,
 * the part of the job no execution covers, fall out of `waitSegments` with no
 * colour of its own.
 *
 * `executions` is plural because a follow-up job is not split: its tasks share
 * one tool-call budget, so one job can hold several.
 */
function asyncJobLane(
  job: LlmTraceAsyncJob,
  executions: LlmTraceSubAgentFlow[],
  delegationLaneId: string,
  parentNodeId: string | null,
): TraceLane {
  const inner = executions.flatMap((flow) => subAgentNodes(flow));
  const envelope: TraceNode = {
    id: `job:${job.job_id}`,
    kind: "async_job",
    label: job.task_ids[0] ?? job.job_id.slice(0, 8),
    startMs: job.offset_start_ms,
    endMs: job.offset_end_ms,
    // What the job spent NOT running its executions: the queue wait before a
    // worker claimed it, plus any teardown after. Same computation the main
    // lane uses to tell a turn's waiting apart from its tools running.
    //
    // Subtracted against the EXECUTION windows, not against the rounds and
    // calls inside them. An execution idling between its own rounds is the
    // sub-agent thinking, which is its business; the job was still running it.
    waitSegments:
      job.offset_start_ms !== null && job.offset_end_ms !== null
        ? subtractIntervals(
            job.offset_start_ms,
            job.offset_end_ms,
            executions.map((flow) => ({
              startMs: flow.offset_start_ms,
              endMs: flow.offset_end_ms,
            })),
          )
        : undefined,
    status: normalizeStatus(job.state),
    rawStatus: job.state,
    durationMs: job.duration_ms,
    openEnded: job.offset_start_ms !== null && job.offset_end_ms === null,
    detail: job as unknown as Record<string, unknown>,
    children: [],
  };

  // The execution's own payload — what it was asked and what it concluded —
  // lives on the flow, so it needs a node of its own to be reachable.
  const executionEnvelopes = executions.map((flow): TraceNode => ({
    id: `sa:${flow.execution_id}`,
    kind: "sa_execution",
    label: `${flow.role} · ${flow.task_id}`,
    startMs: flow.offset_start_ms,
    endMs: flow.offset_end_ms,
    status: normalizeStatus(flow.status),
    rawStatus: flow.status,
    durationMs: flow.duration_ms,
    detail: flow as unknown as Record<string, unknown>,
    children: [],
  }));

  const first = executions[0];
  return {
    id: `job-lane:${job.job_id}`,
    kind: "sub_agent",
    label: first ? `sub-agent: ${first.role}` : "sub-agent",
    // The task id is the only human-meaningful name here, and the job carries
    // it even when no execution ever ran to report one.
    sublabel: job.task_ids.join(", ") || first?.task_id,
    startMs: job.offset_start_ms,
    endMs: job.offset_end_ms,
    parentNodeId,
    parentLaneId: delegationLaneId,
    status: normalizeStatus(job.state),
    rawStatus: job.state,
    // Prefer the execution's payload; fall back to the job so a queued or
    // failed one still opens something (its state and error_payload).
    detail: (first ?? job) as unknown as Record<string, unknown>,
    groupId: delegationLaneId,
    nodes: [envelope, ...executionEnvelopes, ...inner].sort(byStart),
  };
}

/** A sub-agent execution's own work: its rounds and the tools they called. */
function subAgentNodes(flow: LlmTraceSubAgentFlow): TraceNode[] {
  return [
    ...flow.model_turns.map((turn): TraceNode => ({
      id: turn.model_turn_id,
      kind: "sa_model_turn",
      label: `round ${turn.round_index}`,
      startMs: turn.offset_start_ms,
      endMs: turn.offset_end_ms,
      status: normalizeStatus(turn.status),
      rawStatus: turn.status,
      durationMs: turn.latency_ms,
      model: turn.model,
      // The execution and its parent turn ride along so the round's raw IO
      // (and, for older rows, the parent turn's) can be opened from the node.
      detail: {
        ...(turn as unknown as Record<string, unknown>),
        execution_id: flow.execution_id,
        parent_turn_id: flow.parent?.turn_id ?? null,
      },
      tokens: readTokens(mapValue(turn.usage_payload)),
      children: [],
    })),
    ...flow.tool_calls.map((call): TraceNode => ({
      id: call.tool_call_row_id,
      kind: "sa_tool_call",
      label: call.tool_name,
      startMs: call.offset_start_ms,
      endMs: call.offset_end_ms,
      status: normalizeStatus(call.status),
      rawStatus: call.status,
      durationMs: call.duration_ms,
      detail: call as unknown as Record<string, unknown>,
      children: [],
    })),
  ].sort(byStart);
}

function subAgentLaneFromFlow(flow: LlmTraceSubAgentFlow): TraceLane {
  // Model turns and tool calls are kept in one ordered list rather than split
  // into fixed rows, so the renderer stays free to lay them out either way.
  const nodes = subAgentNodes(flow);

  return {
    id: `sa:${flow.execution_id}`,
    kind: "sub_agent",
    label: `sub-agent: ${flow.role}`,
    // task_id is the only human-meaningful name a sub-agent has; without it,
    // three lanes from one dispatch all read "sub-agent: web".
    sublabel: flow.task_id,
    startMs: flow.offset_start_ms,
    endMs: flow.offset_end_ms,
    parentNodeId: flow.parent?.tool_call_id ?? null,
    status: normalizeStatus(flow.status),
    rawStatus: flow.status,
    // What it was asked to do and what it concluded live here, on the flow —
    // none of it appears on the child nodes.
    detail: flow as unknown as Record<string, unknown>,
    groupId: flow.batch_id,
    nodes,
  };
}

export function fromLlmTrace(response: LlmTraceResponse): NormalizedTrace {
  const lanes: TraceLane[] = [];
  let citationIndex = 0;
  let hasTurns = false;
  const tokens = emptyTally();

  // A sub-agent that came from a durable job is drawn inside that job's lane,
  // not as a lane of its own. Index them first: the flows arrive interleaved by
  // start offset, so a delegation can be reached before the executions it
  // spawned. Anything with no job_id took the synchronous path and still gets
  // its own top-level lane.
  const subAgentsByJob = new Map<string, LlmTraceSubAgentFlow[]>();
  for (const flow of response.flows) {
    if (flow.kind !== "sub_agent") continue;
    const jobId = flow.parent?.job_id;
    if (!jobId) continue;
    const bucket = subAgentsByJob.get(jobId);
    if (bucket) bucket.push(flow);
    else subAgentsByJob.set(jobId, [flow]);
  }

  for (const flow of response.flows) {
    if (flow.kind === "async_delegation") {
      const lane = asyncDelegationLaneFromFlow(flow);
      lanes.push(lane);
      for (const job of flow.jobs) {
        const executions = subAgentsByJob.get(job.job_id) ?? [];
        lanes.push(
          asyncJobLane(job, executions, lane.id, flow.parent.tool_call_id),
        );
        for (const execution of executions) {
          for (const turn of execution.model_turns) {
            addUsage(tokens, mapValue(turn.usage_payload));
          }
        }
      }
    } else if (flow.kind === "main") {
      lanes.push(...mainLanesFromFlow(flow));
      hasTurns = flow.turns.length > 0;
      for (const turn of flow.turns) addUsage(tokens, mapValue(turn.usage));
    } else if (flow.kind === "live_session") {
      const normalEnd = ["client_hangup", "session_left", "background", "duration_limit"].includes(flow.ended_reason ?? "");
      const status = !flow.ended_at ? "running" : normalEnd ? "ok" : "error";
      lanes.push({
        id: `live:${flow.live_session_id}`, kind: "live_session", label: "Live call",
        sublabel: `${flow.connection_epochs} client / ${flow.provider_epochs} provider connections`,
        startMs: flow.offset_start_ms, endMs: flow.offset_end_ms,
        parentNodeId: null, status, rawStatus: flow.ended_reason ?? "active",
        detail: flow as unknown as Record<string, unknown>,
        nodes: [{id: `live-envelope:${flow.live_session_id}`, kind: "turn", label: "Live call",
          startMs: flow.offset_start_ms, endMs: flow.offset_end_ms, status,
          rawStatus: flow.ended_reason ?? "active", durationMs:
            flow.offset_start_ms !== null && flow.offset_end_ms !== null
              ? flow.offset_end_ms - flow.offset_start_ms : null,
          detail: flow as unknown as Record<string, unknown>, children: []}],
      });
      // Session usage is already represented by billed turns; do not add it again.
    } else if (flow.kind === "citation_worker") {
      lanes.push(citationLaneFromFlow(flow, citationIndex));
      citationIndex += 1;
      // Citation work is billed too, and it is a real share of TTFT.
      for (const span of flow.spans) addUsage(tokens, mapValue(span.usage));
    } else if (flow.kind === "sub_agent") {
      // Already drawn inside its job's lane, and its usage counted there.
      if (flow.parent?.job_id) continue;
      lanes.push(subAgentLaneFromFlow(flow));
      for (const turn of flow.model_turns) {
        addUsage(tokens, mapValue(turn.usage_payload));
      }
    }
  }

  const timeline = response.timeline ?? {
    first_start_ms: null,
    last_end_ms: null,
    total_span_ms: null,
  };
  // The server nulls every offset at once when it cannot resolve the trigger
  // message, so the whole time axis is unavailable rather than partly missing.
  const hasTimeAxis = timeline.first_start_ms !== null && timeline.last_end_ms !== null;

  const mainLane = lanes.find((lane) => lane.kind === "main");
  const finalTurn = mainLane?.nodes[mainLane.nodes.length - 1];
  const ttftMs = (() => {
    if (!finalTurn) return null;
    const firstTokenAt = stringValue(
      mapValue(finalTurn.detail).first_token_at,
      "",
    );
    if (!firstTokenAt || !response.trigger_message) return null;
    return msBetween(response.trigger_message.created_at, firstTokenAt);
  })();

  // Trigger to answer, not the span of the nodes. `timeline.total_span_ms` ends
  // at the last node, which drops the streaming tail — and the replay side
  // measures trigger-to-answer, so leaving these different would have the diff
  // compare two unlike quantities and make every replay look slower.
  const answerMs = response.trigger_message
    ? msBetween(response.trigger_message.created_at, response.message?.created_at)
    : null;
  const totalMs = answerMs ?? timeline.total_span_ms;

  return {
    source: "llm_trace",
    messageId: response.message?.chat_message_id ?? null,
    // Absent on a v2 response, and on any answer that was never overwritten.
    revisions: (response.report_revisions ?? []).map((revision) => ({
      kind: revision.kind,
      offsetMs: revision.offset_ms,
    })),
    timeline: {
      firstStartMs: timeline.first_start_ms,
      lastEndMs:
        timeline.last_end_ms === null
          ? null
          : Math.max(timeline.last_end_ms, totalMs ?? timeline.last_end_ms),
      answerSpanMs: totalMs,
    },
    hasTimeAxis,
    hasTurns,
    lanes,
    // The trace endpoint reports every flow it has; nothing is out of reach.
    unavailableKinds: [],
    metrics: buildMetrics(lanes, ttftMs, totalMs, tokens),
    degraded: [
      ...(response.trigger_message
        ? []
        : ["트리거 메시지를 찾지 못해 상대 시간을 계산할 수 없습니다."]),
      ...(lanes.some((lane) => lane.kind === "tool_batch") ? [PARALLEL_NOTE] : []),
    ],
  };
}

// ---------------------------------------------------------------------------
// Stored replay run
// ---------------------------------------------------------------------------

const DISPATCH_SCHEMA = "sub_agent_dispatch_result_v1";

/**
 * Sub-agent lanes rebuilt from a stored replay's `delegate_tasks` result.
 *
 * Only for runs recorded before the backend started attaching `sub_agent_flows`.
 * Those runs deleted their session — and every sub_agent_execution row with it —
 * but the replay snapshot keeps the dispatch tool call's result *unslimmed*,
 * and that carries each task's outcome and duration plus every sub-agent tool
 * call in `raw_evidence`.
 *
 * What it cannot recover is *when* each call ran: only durations survive. So
 * the calls are laid end to end from the dispatch start and every node is
 * flagged `approximate` — the widths are measured, the positions are not.
 */
function replaySubAgentLanes(
  toolCalls: unknown[],
  triggerAt: string,
): { lanes: TraceLane[]; sawDispatch: boolean } {
  const lanes: TraceLane[] = [];
  let sawDispatch = false;

  toolCalls.forEach((raw, dispatchIndex) => {
    const call = mapValue(raw);
    const result = mapValue(call.result);
    if (stringValue(result.schema, "") !== DISPATCH_SCHEMA) return;
    sawDispatch = true;

    const batchId = stringValue(result.batch_id, `batch-${dispatchIndex}`);
    // Every task in a batch is launched together, so the dispatch call's own
    // start is the best anchor available for all of them.
    const batchStartMs = msBetween(triggerAt, call.created_at);
    if (batchStartMs === null) return;

    // The brief lives on the dispatch call's own arguments — the executor
    // result echoes findings back but never the objective it was given. Without
    // this the reconstructed lane loses the "what was it asked to do" half that
    // the llm-trace path shows from `request_payload`.
    const requestByTask = new Map<string, Record<string, unknown>>();
    for (const rawTask of arrayValue(mapValue(call.arguments).tasks)) {
      const task = mapValue(rawTask);
      const id = stringValue(task.task_id, "");
      if (id) requestByTask.set(id, task);
    }

    const evidenceByTask = new Map<string, Record<string, unknown>[]>();
    for (const entry of arrayValue(result.raw_evidence)) {
      const evidence = mapValue(entry);
      const taskId = stringValue(evidence.task_id, "");
      const bucket = evidenceByTask.get(taskId);
      if (bucket) bucket.push(evidence);
      else evidenceByTask.set(taskId, [evidence]);
    }

    arrayValue(result.tasks).forEach((rawTask, taskIndex) => {
      const task = mapValue(rawTask);
      const taskId = stringValue(task.task_id, `task-${taskIndex}`);
      const role = stringValue(task.role, "sub-agent");
      // A background dispatch only persists an accepted stub — no state, no
      // duration, nothing to draw.
      const durationMs = intValue(task.duration_ms);
      if (durationMs === null) return;

      const evidence = evidenceByTask
        .get(taskId)
        ?.slice()
        .sort((a, b) => (intValue(a.round_index) ?? 0) - (intValue(b.round_index) ?? 0))
        ?? [];

      // Most of an execution is the model thinking between rounds, and those
      // turns were deleted with the session. Laying the calls back to back from
      // the batch start therefore drew them at a fraction of the real span —
      // 11s of bars for a 58s execution. Spread the unaccounted time across the
      // rounds instead: each round opens with a model turn of unknown length,
      // so give each an equal share. The last call then lands exactly on the
      // measured execution end.
      //
      // Durations are never touched — only the gaps between them are inferred.
      const toolTotalMs = evidence.reduce(
        (sum, item) => sum + (intValue(item.duration_ms) ?? 0),
        0,
      );
      const rounds = [
        ...new Set(evidence.map((item) => intValue(item.round_index) ?? 0)),
      ];
      const slackMs = Math.max(0, durationMs - toolTotalMs);
      const gapMs = rounds.length > 0 ? slackMs / rounds.length : 0;

      let cursorMs = batchStartMs;
      let lastRound: number | null = null;
      const nodes = evidence.map((item, index): TraceNode => {
        const round = intValue(item.round_index) ?? 0;
        if (round !== lastRound) {
          cursorMs += gapMs;
          lastRound = round;
        }
        const callDurationMs = intValue(item.duration_ms) ?? 0;
        const startMs = Math.round(cursorMs);
        cursorMs += callDurationMs;
        return {
          id: stringValue(item.evidence_id, `${taskId}-ev-${index}`),
          kind: "sa_tool_call",
          label: stringValue(item.tool_name, "tool"),
          startMs,
          endMs: startMs + callDurationMs,
          status: item.success === false ? "error" : "ok",
          rawStatus: item.success === false ? "error" : "success",
          durationMs: callDurationMs,
          approximate: true,
          detail: item,
          children: [],
        };
      });

      lanes.push({
        id: `sa:${batchId}:${taskId}`,
        kind: "sub_agent",
        label: `sub-agent: ${role}`,
        sublabel: taskId,
        startMs: batchStartMs,
        endMs: batchStartMs + durationMs,
        parentNodeId: stringValue(call.tool_call_id, "") || null,
        status: normalizeStatus(stringValue(task.state, "")),
        rawStatus: stringValue(task.state, ""),
        // Shaped like a flow so `readExecutorSummary` reads it unchanged.
        detail: {
          task_id: taskId,
          role,
          status: stringValue(task.state, ""),
          request_payload: requestByTask.get(taskId) ?? {},
          result_payload: task,
          error_payload: task.error ?? null,
        },
        groupId: batchId,
        nodes,
      });
    });
  });

  return { lanes, sawDispatch };
}

/** `created_at + latency_ms`, matching how the backend closes out a turn. */
function turnEndMs(startMs: number | null, latencyMs: number | null) {
  if (startMs === null) return null;
  if (latencyMs === null) return startMs;
  return startMs + latencyMs;
}

function replayMainLanes(
  turnChain: unknown[],
  toolCalls: unknown[],
  triggerAt: string,
): TraceLane[] {
  const callsByTurn = new Map<string, Record<string, unknown>[]>();
  const orphanCalls: Record<string, unknown>[] = [];
  for (const raw of toolCalls) {
    const call = mapValue(raw);
    if (Object.keys(call).length === 0) continue;
    const turnId = stringValue(call.turn_id, "");
    if (!turnId) {
      orphanCalls.push(call);
      continue;
    }
    const bucket = callsByTurn.get(turnId);
    if (bucket) bucket.push(call);
    else callsByTurn.set(turnId, [call]);
  }

  const nodes = turnChain.map((raw, index): TraceNode => {
    const turn = mapValue(raw);
    const turnId = stringValue(turn.turn_id, `turn-${index}`);
    const startMs = msBetween(triggerAt, turn.created_at);
    const latencyMs = intValue(turn.latency_ms);
    const endMs = turnEndMs(startMs, latencyMs);

    const calls = (callsByTurn.get(turnId) ?? [])
      .slice()
      .sort((a, b) => (intValue(a.sequence) ?? 0) - (intValue(b.sequence) ?? 0));

    const children = calls.map((call, callIndex): TraceNode => {
      const callStartMs = msBetween(triggerAt, call.created_at);
      // Neither a duration nor a completion time means the call never came
      // back. `durationMsOf` would report 0, which reads as "instant" — the
      // opposite of what happened.
      const unfinished =
        intValue(call.duration_ms) === null && !stringValue(call.completed_at, "");
      const durationMs = unfinished ? null : durationMsOf(call);
      return {
        id: stringValue(call.tool_call_id, `${turnId}-call-${callIndex}`),
        kind: "tool_call",
        label: stringValue(call.tool_name, "tool"),
        startMs: callStartMs,
        endMs:
          callStartMs === null || durationMs === null
            ? null
            : callStartMs + durationMs,
        status: normalizeStatus(stringValue(call.status, "")),
        rawStatus: stringValue(call.status, ""),
        durationMs,
        openEnded: callStartMs !== null && unfinished,
        detail: call,
        children: [],
      };
    });

    // Same rule the backend uses: inference stops at the first tool call, or at
    // the end of the turn when the turn called no tools.
    const firstCallStart = children.reduce<number | null>((min, child) => {
      if (child.startMs === null) return min;
      return min === null || child.startMs < min ? child.startMs : min;
    }, null);

    const finishReason = stringValue(turn.finish_reason, "");
    return {
      id: turnId,
      kind: "turn",
      label: `turn ${intValue(turn.turn_index) ?? index}`,
      startMs,
      endMs,
      inferenceEndMs: firstCallStart ?? endMs,
      waitSegments: waitSegmentsFor(firstCallStart ?? endMs, endMs, children),
      status: finishReason === "error" ? "error" : "ok",
      rawStatus: finishReason,
      durationMs: latencyMs,
      model: stringValue(turn.model, "") || undefined,
      provider: stringValue(turn.provider, "") || undefined,
      finalizeAnswer: finishReason === "tool_use" && children.length === 0,
      openEnded: startMs !== null && endMs === null,
      detail: turn,
      children,
    };
  });

  // Calls the snapshot could not attach to a turn still belong on the timeline.
  for (const call of orphanCalls) {
    const startMs = msBetween(triggerAt, call.created_at);
    const unfinished =
      intValue(call.duration_ms) === null && !stringValue(call.completed_at, "");
    const durationMs = unfinished ? null : durationMsOf(call);
    nodes.push({
      id: stringValue(call.tool_call_id, `orphan-${nodes.length}`),
      kind: "tool_call",
      label: stringValue(call.tool_name, "tool"),
      startMs,
      endMs:
        startMs === null || durationMs === null ? null : startMs + durationMs,
      status: normalizeStatus(stringValue(call.status, "")),
      rawStatus: stringValue(call.status, ""),
      durationMs,
      openEnded: startMs !== null && unfinished,
      detail: call,
      children: [],
    });
  }

  const bounds = laneBounds(nodes);
  return [
    {
      id: "main",
      kind: "main",
      label: "main",
      startMs: bounds.startMs,
      endMs: bounds.endMs,
      parentNodeId: null,
      nodes,
    },
    ...promoteBatches(nodes),
  ];
}

function replayCitationLanes(
  spans: unknown[],
  triggerAt: string,
): TraceLane[] {
  const byRun = new Map<string, TraceNode[]>();
  const order: string[] = [];

  spans.forEach((raw, index) => {
    const span = mapValue(raw);
    if (Object.keys(span).length === 0) return;
    const runId = stringValue(span.worker_run_id, "cw-unknown");
    const startMs = msBetween(triggerAt, span.started_at);
    const endMs = msBetween(triggerAt, span.completed_at);
    const node: TraceNode = {
      id: stringValue(span.span_id, `${runId}-span-${index}`),
      kind: "cw_span",
      label: stringValue(span.span_kind, "span"),
      startMs,
      endMs,
      status: normalizeStatus(stringValue(span.status, "")),
      rawStatus: stringValue(span.status, ""),
      durationMs: intValue(span.duration_ms) ?? durationOf(startMs, endMs),
      model: stringValue(span.model, "") || undefined,
      provider: stringValue(span.provider, "") || undefined,
      detail: span,
      children: [],
    };
    const bucket = byRun.get(runId);
    if (bucket) {
      bucket.push(node);
    } else {
      byRun.set(runId, [node]);
      order.push(runId);
    }
  });

  return order.map((runId, index) => {
    const nodes = (byRun.get(runId) ?? []).sort(byStart);
    const bounds = laneBounds(nodes);
    return {
      id: `cw:${runId}`,
      kind: "citation_worker" as const,
      label: `citation #${index + 1}`,
      startMs: bounds.startMs,
      endMs: bounds.endMs,
      // The snapshot keeps parent_llm_turn_id / llm_tool_call_id when present.
      parentNodeId:
        stringValue(
          mapValue(nodes[0]?.detail).llm_tool_call_id,
          stringValue(mapValue(nodes[0]?.detail).parent_llm_turn_id, ""),
        ) || null,
      nodes,
    };
  });
}

/**
 * Rebuild a trace from a replay run, which `llm-trace` cannot serve.
 *
 * The snapshot stores timestamps rather than offsets, so offsets are recomputed
 * against the replay's own trigger message. Main and citation-worker lanes come
 * out at full fidelity; sub-agent rows were deleted with the replay session and
 * are gone for good, which is what `degraded` records.
 */
export function fromReplaySnapshot(
  replay: Record<string, unknown>,
): NormalizedTrace {
  const trigger = mapValue(replay.trigger_message);
  const assistant = mapValue(replay.assistant_message);
  const latency = mapValue(replay.latency);
  const triggerAt = stringValue(trigger.created_at, "");

  const degraded = ["replay 스냅샷에는 턴별 reasoning과 usage가 없습니다."];

  if (!triggerAt) {
    return {
      source: "replay_snapshot",
      messageId: stringValue(assistant.chat_message_id, "") || null,
      // A replay is never a delegated answer, so its body was never replaced.
      revisions: [],
      timeline: { firstStartMs: null, lastEndMs: null, answerSpanMs: null },
      hasTimeAxis: false,
      hasTurns: false,
      lanes: [],
      metrics: buildMetrics([], null, null, emptyTally()),
      unavailableKinds: [],
      degraded: [
        "replay 트리거 메시지가 없어 상대 시간을 계산할 수 없습니다.",
        ...degraded,
      ],
    };
  }

  // Saved runs nest these under `result`; the live replay response puts them at
  // the top level.
  const fromTopOrResult = (key: string) => {
    const top = arrayValue(replay[key]);
    if (top.length > 0) return top;
    return arrayValue(mapValue(replay.result)[key]);
  };

  const toolCalls = arrayValue(replay.tool_calls);
  const unavailableKinds: TraceLaneKind[] = [];

  // Measured flows when the backend attached them — the same objects the trace
  // endpoint returns, so they go through the same lane builder and the two tabs
  // render identically by construction. Older runs predate that and fall back
  // to rebuilding what the dispatch result kept.
  const subAgentFlows = fromTopOrResult("sub_agent_flows");
  const subAgentLanes = (() => {
    if (subAgentFlows.length > 0) {
      return subAgentFlows.map((flow) =>
        subAgentLaneFromFlow(flow as unknown as LlmTraceSubAgentFlow),
      );
    }
    const rebuilt = replaySubAgentLanes(toolCalls, triggerAt);
    if (rebuilt.lanes.length > 0) {
      degraded.push(
        "sub-agent 도구 호출의 가로 위치는 재구성값입니다 (길이는 실측). model turn 기록은 없습니다.",
      );
    } else if (rebuilt.sawDispatch) {
      // A dispatch happened but nothing was recoverable — background tasks
      // report back under a schema with no batch id to join on.
      unavailableKinds.push("sub_agent");
      degraded.push(
        "이 replay의 sub-agent 실행 기록은 복원할 수 없습니다 (replay 세션이 삭제됨).",
      );
    }
    return rebuilt.lanes;
  })();

  const lanes: TraceLane[] = [
    ...replayMainLanes(arrayValue(replay.turn_chain), toolCalls, triggerAt),
    ...replayCitationLanes(fromTopOrResult("citation_worker_spans"), triggerAt),
    ...subAgentLanes,
  ].sort((a, b) => {
    // main first, then by start offset — the order the trace endpoint uses.
    if (a.kind === "main") return -1;
    if (b.kind === "main") return 1;
    // Batch lanes stay with the main loop they belong to, matching the order
    // `fromLlmTrace` produces, so the two tabs read the same.
    if (a.kind !== b.kind) {
      if (a.kind === "tool_batch") return -1;
      if (b.kind === "tool_batch") return 1;
    }
    return (a.startMs ?? Number.MAX_SAFE_INTEGER) - (b.startMs ?? Number.MAX_SAFE_INTEGER);
  });

  if (lanes.some((lane) => lane.kind === "tool_batch")) degraded.push(PARALLEL_NOTE);

  const bounds = laneBounds(lanes.flatMap((lane) => lane.nodes));
  const totalMs =
    intValue(latency.total_elapsed_to_message_ms) ??
    msBetween(triggerAt, assistant.created_at) ??
    durationOf(bounds.startMs, bounds.endMs);

  // The axis starts at the trigger, not at the first turn. Anchoring it to the
  // earliest node would hide the setup gap in front of turn 0 — prompt
  // preparation and routing, which the old latency chart showed as its
  // `replay_setup` phase. The trailing gap after the last turn is the answer
  // streaming, so the axis runs to the assistant message when that is later.
  const lastEndMs =
    bounds.endMs === null
      ? totalMs
      : Math.max(bounds.endMs, totalMs ?? bounds.endMs);

  return {
    source: "replay_snapshot",
    messageId: stringValue(assistant.chat_message_id, "") || null,
    revisions: [],
    timeline: {
      firstStartMs: lastEndMs === null ? null : 0,
      lastEndMs,
      answerSpanMs: lastEndMs,
    },
    hasTimeAxis: lastEndMs !== null,
    hasTurns: lanes.some(
      (lane) => lane.kind === "main" && lane.nodes.some((node) => node.kind === "turn"),
    ),
    lanes,
    unavailableKinds,
    // The snapshot carries no usage payloads at all, so every token count stays
    // null rather than reading as a genuine zero.
    metrics: buildMetrics(
      lanes,
      intValue(latency.total_ttft_ms),
      totalMs,
      emptyTally(),
    ),
    degraded,
  };
}
